'use client'
import { FC, useEffect } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";


declare global {
  interface Window {
    EmbedManager?: {
      embed: (options: { key: string; width: string }) => void;
    };
  }
}

/**
 * Props for `FormsEmbedCode`.
 */
export type FormsEmbedCodeProps =
  SliceComponentProps<Content.FormsEmbedCodeSlice>;

/**
 * Component for "FormsEmbedCode" Slices.
 */
const FormsEmbedCode: FC<FormsEmbedCodeProps> = ({ slice }) => {
  const embeddedForm = slice.primary.embed_code?.embed_url || "";
  const formsiteId = slice.primary.formsite_id || "";
  const rawHtmlIframe = slice.primary.raw_html_iframe || "";
  const includeFormsiteCode = Boolean(slice.primary.include_formsite_code);

  const hasFormsite = includeFormsiteCode && Boolean(formsiteId);
  const hasEmbedUrl = !hasFormsite && Boolean(embeddedForm);
  const hasRawHtml = !hasFormsite && !hasEmbedUrl && Boolean(rawHtmlIframe);

  useEffect(() => {
    if (hasFormsite) {
      const script = document.createElement("script");
      script.src = "https://fs4.formsite.com/include/form/embedManager.js?"+formsiteId.split("&").pop();
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        if (window.EmbedManager) {
          window.EmbedManager.embed({
            key: `https://fs4.formsite.com/res/showFormEmbed?EParam=${formsiteId}`,
            width: "100%",
          });
        }
      };

      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [hasFormsite, formsiteId]);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className=" !bg-[#f2f2f2] font-fellix px-4 md:px-8 lg:px-12"
    >
      <div className="max-w-[1280px] mx-auto py-16">
        {hasEmbedUrl && (
          <div className="iframe-area">
            <iframe src={embeddedForm} title="Embedded Form" className="border-none"></iframe>
          </div>
        )}
        {hasFormsite && (
          <div className="!bg-[#f2f2f2] my-12 py-24" dangerouslySetInnerHTML={{
              __html: `<a name="form${formsiteId.split("&").pop()}" id="formAnchor${formsiteId.split("&").pop()}"></a>`,
            }}
          />
        )}
        {hasFormsite && (
          <div className="form-area-formsite outline-none appearance-none !font-yellix" >
            <a
              {...({
                name: `form${formsiteId.split("&").pop()}`,
                id: `formAnchor${formsiteId.split("&").pop()}`
              } as any)}
            ></a>
          </div>
        )}
        {hasRawHtml && (
          <div className="!bg-[#f2f2f2]"
            dangerouslySetInnerHTML={{
              __html: rawHtmlIframe,
            }}
          />
        )}
      </div>
    </section>
  );
};

export default FormsEmbedCode;


